import React, { useState, useRef } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import SCORE_RUBRICS from '../../config/scoreRubrics';

/**
 * Reusable card used in the 5-parameter score breakdown.
 * Shows:
 * - emoji icon
 * - label + eye icon; rubric shows on hover over the eye icon
 * - max score
 * - actual value
 * - progress bar
 * - optional rubric panel (hover to view)
 */

function ScoreParameterCard({ paramKey, label, max, emoji, bgClass, value }) {
  const [showRubric, setShowRubric] = useState(false);
  const hideTimerRef = useRef(null);
  const rubric = SCORE_RUBRICS[paramKey];

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHideTimer();
    setShowRubric(true);
  };

  const handleMouseLeave = () => {
    hideTimerRef.current = setTimeout(() => setShowRubric(false), 100);
  };

  const percentage = Math.min(((value ?? 0) / max) * 100, 100);

  return (
    <div className="group hover:bg-gray-50 transition-colors px-2.5 py-2 rounded-lg border border-gray-100">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-[11px]">{emoji}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <div className="font-semibold text-[13px] text-gray-900 truncate">{label}</div>
              {rubric && (
                <span
                  className="relative flex-shrink-0 inline-flex"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  role="img"
                  aria-label={`Show rubric for ${label}`}
                >
                  <span className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors duration-150">
                    <EyeIcon className="h-3 w-3" />
                  </span>
                  <div
                    className={`absolute left-full top-1/2 -translate-y-1/2 z-20 ml-1.5 min-w-[200px] max-w-[260px] border border-gray-200 rounded bg-gray-50 px-1.5 py-1 shadow-md transition-all duration-150 ease-out ${
                      showRubric
                        ? 'opacity-100 translate-x-0 pointer-events-auto'
                        : 'opacity-0 -translate-x-1 pointer-events-none'
                    }`}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="text-[10px] font-semibold text-gray-700 mb-0.5">
                      {rubric.title} rubric
                    </div>
                    <ul className="space-y-0.5">
                      {rubric.ranges.map((band) => (
                        <li key={`${paramKey}-${band.range}`} className="text-[10px] text-gray-600">
                          <span className="font-semibold">{band.range} → </span>
                          {band.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="ml-2 flex-shrink-0 text-right">
          <div className="text-base font-extrabold text-indigo-700 leading-none">{value ?? 0}</div>
          <div className="text-[11px] text-gray-600 leading-none mt-0.5">/ {max}</div>
        </div>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
        <div
          className={`bg-gradient-to-r ${bgClass} h-1 rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default ScoreParameterCard;

